import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useDispatch, useSelector } from "react-redux";
import {mainSelector, RoomFloorMapsModalOpen, SwitchRoom} from "../../store/slices/main.slice";
import {useTranslation} from "react-i18next";

const FloorMapsDrawerRoot = () => {
    const drawerRef = useRef(null);
    const dispatch: any = useDispatch();
    const { roomFloorMapsModal, currentRoomIndex } = useSelector(mainSelector);
    const { t, i18n }: any = useTranslation();

    const navImage = (floor: string, active: boolean) => {
        const suffix = ({ en: '_ENG', fr: '_FRA', de: '_TED' } as Record<string, string>)[i18n.language?.slice(0, 2)] ?? '';
        return `/images/floor-navigation/planim_${floor}${suffix}${active ? '_on' : ''}.png`;
    }

    useEffect(() => {
        dispatch(RoomFloorMapsModalOpen(false));
    }, []);

    const onClose = () => {
        //@ts-ignore
        dispatch(RoomFloorMapsModalOpen(false));
    }

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            //@ts-ignore
            if (drawerRef.current && !drawerRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleRoomChange = (room: number) => {
        let url = new URL(window.location as any);
        url.searchParams.set('room', room.toString());
        // eslint-disable-next-line
        history.replaceState(null, '', url);
        dispatch(SwitchRoom(room))
    }

    const getFloorIndex = () => {
        switch (currentRoomIndex) {
            case 1:
            case 2:
            case 3:
            case 4:
            case 5:
            case 29:
                return 0
            case 30:
            case 31:
            case 32:
            case 33:
            case 34:
            case 35:
            case 36:
            case 37:
            case 38:
            case 39:
            case 40:
            case 41:
            case 42:
            case 43:
            case 44:
                return 1
            case 6:
            case 7:
            case 8:
            case 9:
            case 10:
            case 11:
            case 12:
            case 13:
            case 14:
            case 15:
            case 16:
            case 17:
            case 18:
            case 19:
            case 20:
                return 2
            case 21:
            case 22:
            case 23:
            case 24:
            case 25:
            case 26:
            case 27:
            case 28:
                return -1
        }
    }
    const handleFloorClick = (floorIndex: number) => {
        switch (floorIndex) {
            case -1:
                handleRoomChange(21);
                onClose();
                break;
            case 0:
                handleRoomChange(1);
                onClose();
                break;
            case 1:
                handleRoomChange(30);
                onClose();
                break;
            case 2:
                handleRoomChange(6);
                onClose();
                break;
        }
    }

    type RotatedProps = { onClose: () => void; children: React.ReactNode };

    function useElementSize<T extends HTMLElement>() {
        const ref = React.useRef<T | null>(null);
        const [size, setSize] = React.useState({ w: 0, h: 0 });

        React.useEffect(() => {
            if (!ref.current) return;
            const el = ref.current;
            const ro = new ResizeObserver(([entry]) => {
                const { width, height } = entry.contentRect;
                setSize({ w: Math.round(width), h: Math.round(height) });
            });
            ro.observe(el);
            return () => ro.disconnect();
        }, []);

        return { ref, ...size };
    }

    const RotatedMobileContainer: React.FC<RotatedProps> = ({ onClose, children }) => {
        const { ref: outerRef, w: pw, h: ph } = useElementSize<HTMLDivElement>();
        const ready = pw > 0 && ph > 0;

        // Base pre-rotazione: la mettiamo "swappata"
        const baseW = ph; // width prima della rotazione
        const baseH = pw; // height prima della rotazione

        // Dopo rotate(90deg):
        // larghezza visiva = baseH, altezza visiva = baseW
        const scale = ready ? Math.min(pw / baseH, ph / baseW) : 1;

        const outerStyle: React.CSSProperties = {
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden', // va bene, non sforeremo
        };

        const rotatedStyle: React.CSSProperties = {
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: `${baseW}px`,
            height: `${baseH}px`,
            transform: `translate(-50%, -50%) rotate(90deg) scale(${scale})`,
            transformOrigin: 'center center',
            boxSizing: 'border-box',
            padding: '40px',
            display: 'flex'
        };

        return (
            <div ref={outerRef} style={outerStyle} onClick={onClose}>
                {ready && (
                    <div style={rotatedStyle} onClick={(e) => e.stopPropagation()}>
                        {children}
                    </div>
                )}
            </div>
        );
    };

    return (
        <AnimatePresence>
            {roomFloorMapsModal.open && (
                <motion.div
                    ref={drawerRef}
                    key="floor-maps-drawer--root"
                    className="fixed top-0 right-0 bottom-0 w-[100vw] lg:w-[500px] bg-[#302e2b] z-[100] h-[100vh overflow-y-auto"
                    initial={{x: '100%'}}
                    animate={{x: 0}}
                    exit={{x: '100%'}}
                    transition={{duration: 0.5}}
                >
                    <div className="hidden lg:flex p-[20px] lg:p-[40px] flex-col h-full">
                        {/* Bottone di chiusura */}
                        <button
                            onClick={onClose}
                            className="w-max h-max max-w-[45px] max-h-[45px] flex sm:hidden lg:flex items-center justify-center mb-[40px]"
                        >
                            <img
                                src="/images/close-drawer-icon.png"
                                alt="Close icon"
                                className="max-h-[20px] w-auto"
                            />
                        </button>

                        {/* Titolo */}
                        <div className="w-full flex flex-row items-center justify-between">
                            <h4 className="text-white text-xs lg:text-base font-untitledSans w-full mb-[20px] lg:mb-[40px]">
                                {t('vt.virtual_tour_floor_maps_drower')}:
                            </h4>
                            <button
                                onClick={onClose}
                                className="w-max h-max max-w-[45px] max-h-[45px] hidden sm:flex lg:hidden items-center justify-center"
                            >
                                <img
                                    src="/images/close-drawer-icon.png"
                                    alt="Close icon"
                                    className="max-h-[20px] w-auto"
                                />
                            </button>
                        </div>

                        {/* Contenitore delle mappe */}
                        <div
                            id="maps-container"
                            className="flex flex-col sm:grid sm:grid-cols-2 sm:gap-4 lg:flex h-full w-full overflow-hidden"
                        >
                            {/* Mappa 1 */}
                            {getFloorIndex() !== 2 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(2)}
                                >
                                    <img
                                        src={navImage('piano2', false)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                            {getFloorIndex() === 2 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(2)}
                                >
                                    <img
                                        src={navImage('piano2', true)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}

                            {/* Mappa 2 */}
                            {getFloorIndex() !== 1 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(1)}
                                >
                                    <img
                                        src={navImage('piano1', false)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                            {getFloorIndex() === 1 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(1)}
                                >
                                    <img
                                        src={navImage('piano1', true)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}

                            {/* Mappa 3 */}
                            {getFloorIndex() !== 0 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(0)}
                                >
                                    <img
                                        src={navImage('piano0', false)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                            {getFloorIndex() === 0 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(0)}
                                >
                                    <img
                                        src={navImage('piano0', true)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}

                            {/* Mappa 4 */}
                            {getFloorIndex() !== -1 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(-1)}
                                >
                                    <img
                                        src={navImage('piano_sott', false)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                            {getFloorIndex() === -1 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(-1)}
                                >
                                    <img
                                        src={navImage('piano_sott', true)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="portrait-lock flex lg:hidden p-[20px] lg:p-[40px] flex-col h-full">
                        {/* Bottone di chiusura */}
                        <div className="flex flex-row items-center justify-between pt-[10px] pb-[20px]">
                            <img src="/images/GNDM logo full.png" alt="Header GNDM Logo" className="max-h-[30px] cursor-pointer hidden lg:block"/>
                            <img src="/images/GNDM acronimo orizzontale white.png" alt="Header GNDM Logo" className="max-h-[20px] cursor-pointer lg:hidden block"/>
                            <button onClick={onClose} className="text-white text-md font-untitledSans leading-[15px]">X</button>
                        </div>

                        <h4 className="text-white text-xs lg:text-base font-untitledSans w-full mb-[20px] lg:mb-[40px]">
                            {t('vt.virtual_tour_floor_maps_drower')}:
                        </h4>

                        {/* Contenitore delle mappe */}
                        <div
                            id="maps-container"
                            className="flex flex-col h-full w-full overflow-hidden"
                        >
                            {/* Mappa 1 */}
                            {getFloorIndex() !== 2 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(2)}
                                >
                                    <img
                                        src={navImage('piano2', false)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                            {getFloorIndex() === 2 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(2)}
                                >
                                    <img
                                        src={navImage('piano2', true)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}

                            {/* Mappa 2 */}
                            {getFloorIndex() !== 1 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(1)}
                                >
                                    <img
                                        src={navImage('piano1', false)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                            {getFloorIndex() === 1 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(1)}
                                >
                                    <img
                                        src={navImage('piano1', true)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}

                            {/* Mappa 3 */}
                            {getFloorIndex() !== 0 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(0)}
                                >
                                    <img
                                        src={navImage('piano0', false)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                            {getFloorIndex() === 0 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(0)}
                                >
                                    <img
                                        src={navImage('piano0', true)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}

                            {/* Mappa 4 */}
                            {getFloorIndex() !== -1 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(-1)}
                                >
                                    <img
                                        src={navImage('piano_sott', false)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                            {getFloorIndex() === -1 && (
                                <div
                                    className="flex flex-grow min-h-0 items-center justify-center cursor-pointer"
                                    onClick={() => handleFloorClick(-1)}
                                >
                                    <img
                                        src={navImage('piano_sott', true)}
                                        alt="Map"
                                        className="h-full max-h-full w-auto object-contain"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );


};

export default FloorMapsDrawerRoot;
